import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetchEntitlement,
  createCheckoutSession,
  fetchCheckoutStatus,
} from "@/lib/api";

/**
 * Lifetime-access entitlement, keyed off the anonymous (or Google-auth'd)
 * user id. Returns { lifetime: boolean, isLoading }. We refetch on window
 * focus so a successful purchase on a Stripe tab is reflected here without
 * a hard reload.
 */
export function useEntitlement() {
  const q = useQuery({
    queryKey: ["entitlement"],
    queryFn: fetchEntitlement,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  return {
    lifetime: !!q.data?.lifetime,
    isLoading: q.isLoading,
    refetch: q.refetch,
  };
}

/**
 * Kick off a Stripe Checkout redirect for the lifetime package. The
 * frontend only ever sends its origin — never a price — so manipulation
 * on the wire can't change the £20 amount.
 */
export function useStartCheckout() {
  return useMutation({
    mutationFn: async () => {
      const data = await createCheckoutSession(window.location.origin, "lifetime");
      // Hand off to Stripe — they handle the actual payment UI.
      window.location.href = data.url;
      return data;
    },
  });
}

/**
 * Used on the welcome page when Stripe redirects us back with
 * ?session_id=…. Polls /checkout/status up to ~30s. When the row goes
 * to `paid`, the backend has already upserted the entitlement row.
 *
 * Errors from the network or the Stripe SDK no longer poison the
 * promise — they're caught per-iteration so a transient failure
 * doesn't strand the UI on "Checking your payment".
 */
export async function pollCheckoutStatus(sessionId, { maxAttempts = 15, intervalMs = 2000 } = {}) {
  let lastErr = null;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const s = await fetchCheckoutStatus(sessionId);
      if (s.payment_status === "paid") return { ok: true, status: s };
      if (s.status === "expired") return { ok: false, status: s, reason: "expired" };
    } catch (e) {
      lastErr = e;
      // Surface in console so it's debuggable without poisoning state.
      console.warn("checkout/status poll failed", e?.response?.status, e?.message);
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { ok: false, reason: lastErr ? "error" : "timeout", error: lastErr };
}
